/**
 * Refresh intent for a SHARED listing query (review r2 MAJOR 4). Two tabs can
 * reach the same prefix and share one TanStack query; `resetQueries` then runs
 * whichever observer's `queryFn` it likes, so an intent kept in an observer-
 * local ref is lost whenever the other observer fetches. The intent therefore
 * belongs to the query: it is keyed by the query key's hash and consumed by
 * whichever observer runs the FIRST-page fetch. "Refresh parent" marks the
 * parent listing's key before navigating, so the parent lists with
 * `bypassCache` too (spec §9 `azure-storage:list` row).
 */
import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import type { StorageExplorerMode, StorageExplorerView } from '../../core/types/workspaceTypes';
import { buildStorageListQuery, storageListingQueryKey } from './storageListingKeys';

const pendingRefresh = new Set<string>();

/** Consumed by the next first-page fetch of that query, whoever runs it. */
export function markStorageRefresh(queryKey: QueryKey): void {
  pendingRefresh.add(hashKey(queryKey));
}

/** True exactly once per mark. */
export function consumeStorageRefresh(queryKey: QueryKey): boolean {
  return pendingRefresh.delete(hashKey(queryKey));
}

/** An explicit user Refresh of one listing query: mark the intent, then drop + refetch the shared query. */
export function refreshStorageQuery(queryClient: QueryClient, queryKey: QueryKey): void {
  markStorageRefresh(queryKey);
  void queryClient.resetQueries({ queryKey, exact: true });
}

/** The same for a listing the caller is about to navigate to (e.g. the parent of a vanished folder); `null` when that location has no listing. */
export function refreshStorageListingAt(queryClient: QueryClient, connectionId: string, mode: StorageExplorerMode, resource: StorageResourceRef, view: StorageExplorerView): boolean {
  const query = buildStorageListQuery(mode, resource, view);
  if (!query) return false;
  refreshStorageQuery(queryClient, storageListingQueryKey(connectionId, resource, query));
  return true;
}
